/**
 * Onboarding Service
 * Maneja el estado del onboarding en localStorage para detectar primera sesión
 */

const ONBOARDING_KEY = 'zonavirtual_onboarding';

export interface OnboardingPreferences {
  interests: string[];
  experience: string;
  goals: string[];
}

export interface OnboardingData {
  userId: string;
  completed: boolean;
  completedAt?: string;
  skipped: boolean;
  skippedAt?: string;
  preferences?: OnboardingPreferences;
}

/**
 * Obtiene los datos del onboarding desde localStorage
 */
const getOnboardingData = (userId: string): OnboardingData | null => {
  try {
    const stored = localStorage.getItem(`${ONBOARDING_KEY}_${userId}`);
    if (!stored) return null;
    return JSON.parse(stored);
  } catch (error) {
    console.error('Error reading onboarding data:', error);
    return null;
  }
};

/**
 * Guarda los datos del onboarding en localStorage
 */
const setOnboardingData = (userId: string, data: OnboardingData): void => {
  try {
    localStorage.setItem(`${ONBOARDING_KEY}_${userId}`, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving onboarding data:', error);
  }
};

/**
 * Verifica si el usuario ha completado el onboarding
 */
export const hasCompletedOnboarding = (userId: string): boolean => {
  const data = getOnboardingData(userId);
  return data?.completed === true;
};

/**
 * Verifica si el usuario ha saltado el onboarding
 */
export const hasSkippedOnboarding = (userId: string): boolean => {
  const data = getOnboardingData(userId);
  return data?.skipped === true;
};

/**
 * Determina si se debe mostrar el onboarding al usuario
 * Retorna true si es la primera vez (no completado y no saltado)
 */
export const shouldShowOnboarding = (userId: string): boolean => {
  if (!userId) return false;
  
  const data = getOnboardingData(userId);
  
  // Si no hay datos, es primera vez
  if (!data) return true;
  
  // Si ya completó o saltó, no mostrar
  if (data.completed || data.skipped) return false;
  
  return true;
};

/**
 * Marca el onboarding como completado
 */
export const markOnboardingAsCompleted = (userId: string): void => {
  const existingData = getOnboardingData(userId) || {
    userId,
    completed: false,
    skipped: false
  };

  const updatedData: OnboardingData = {
    ...existingData,
    completed: true,
    completedAt: new Date().toISOString(),
    skipped: false
  };

  setOnboardingData(userId, updatedData);
  console.log('✅ Onboarding marked as completed for user:', userId);
};

/**
 * Marca el onboarding como saltado
 */
export const skipOnboarding = (userId: string): void => {
  const existingData = getOnboardingData(userId) || {
    userId,
    completed: false,
    skipped: false
  };

  const updatedData: OnboardingData = {
    ...existingData,
    skipped: true,
    skippedAt: new Date().toISOString()
  };

  setOnboardingData(userId, updatedData);
  console.log('⏭️ Onboarding skipped for user:', userId);
};

/**
 * Guarda las preferencias del usuario del onboarding
 */
export const saveUserPreferences = (userId: string, preferences: Partial<OnboardingPreferences>): void => {
  const existingData = getOnboardingData(userId) || {
    userId,
    completed: false,
    skipped: false
  };

  const updatedPreferences: OnboardingPreferences = {
    interests: preferences.interests || existingData.preferences?.interests || [],
    experience: preferences.experience || existingData.preferences?.experience || '',
    goals: preferences.goals || existingData.preferences?.goals || []
  };

  const updatedData: OnboardingData = {
    ...existingData,
    preferences: updatedPreferences
  };

  setOnboardingData(userId, updatedData);
  console.log('💾 User preferences saved:', updatedPreferences);
};

/**
 * Obtiene las preferencias guardadas del usuario
 */
export const getUserPreferences = (userId: string): OnboardingPreferences | null => {
  const data = getOnboardingData(userId);
  return data?.preferences || null;
};

/**
 * Reinicia el estado del onboarding (útil para testing)
 */
export const resetOnboarding = (userId: string): void => {
  try {
    localStorage.removeItem(`${ONBOARDING_KEY}_${userId}`);
    console.log('🔄 Onboarding reset for user:', userId);
  } catch (error) {
    console.error('Error resetting onboarding:', error);
  }
};

/**
 * Obtiene estadísticas del onboarding (para debugging)
 */
export const getOnboardingStats = (userId: string): OnboardingData | null => {
  return getOnboardingData(userId);
};

